import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  message,
} from 'antd';
import AdaptiveTable from './AdaptiveTable';
import {
  cartonApi,
  customerApi,
  parseProductKey,
  productApi,
  productKey,
  supplierApi,
  type CartonBatchDTO,
  type CartonProductItemDTO,
  type CreateCartonItemInBatch,
  type ProductDTO,
} from '../api/kfc';
import CargoNameSelect, { cargoNameFormItemProps } from './CargoNameSelect';

interface BatchCartonRow {
  cartonId: string;
  manualEdited: boolean;
  products: CartonProductItemDTO[];
}

function generateCartonId(batchId: string, index: number): string {
  const base = (batchId || '').trim();
  if (!base) return '';
  return `${base}-${index + 1}`;
}

function buildCartonRows(batchId: string, boxCount: number, prev: BatchCartonRow[]): BatchCartonRow[] {
  const count = Math.max(1, boxCount);
  const rows: BatchCartonRow[] = [];
  for (let i = 0; i < count; i++) {
    const existing = prev[i];
    const autoId = generateCartonId(batchId, i);
    if (existing?.manualEdited) {
      rows.push({ ...existing });
    } else {
      rows.push({
        cartonId: autoId,
        manualEdited: false,
        products: existing?.products ?? [],
      });
    }
  }
  return rows;
}

export interface CartonBatchFormModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (batch: CartonBatchDTO) => void;
  /** 传入后客户锁定为该值（运单内新增批次） */
  fixedClientId?: string;
  /** 传入后创建即绑定该运单 */
  shipmentId?: string;
  /** 批次标识默认值 */
  defaultBatchId?: string;
  title?: string;
}

/** 录入货物批次：默认只建批次（件数）；高级选项可逐箱录入箱号与产品 */
const CartonBatchFormModal: React.FC<CartonBatchFormModalProps> = ({
  open,
  onClose,
  onCreated,
  fixedClientId,
  shipmentId,
  defaultBatchId,
  title,
}) => {
  const [form] = Form.useForm();
  const [suppliers, setSuppliers] = useState<{ value: string; label: string }[]>([]);
  const [customers, setCustomers] = useState<{ value: string; label: string }[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductDTO[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [batchId, setBatchId] = useState('');
  const [boxCount, setBoxCount] = useState(1);
  const [advanced, setAdvanced] = useState(false);
  const [cartonRows, setCartonRows] = useState<BatchCartonRow[]>([
    { cartonId: '', manualEdited: false, products: [] },
  ]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || optionsLoaded) return;
    setOptionsLoaded(true);
    supplierApi.list().then((res) => {
      setSuppliers((res.data || []).map((s) => ({ value: s.id!, label: `${s.supplierName || s.supplierId || s.id}` })));
    }).catch(() => setSuppliers([]));
    customerApi.list().then((res) => {
      setCustomers((res.data || []).map((c) => ({ value: c.id!, label: `${c.name || c.customerCode || c.id}` })));
    }).catch(() => setCustomers([]));
  }, [open, optionsLoaded]);

  useEffect(() => {
    if (!open) return;
    const initialBatchId = defaultBatchId || '';
    setBatchId(initialBatchId);
    setBoxCount(1);
    setAdvanced(false);
    setCartonRows(buildCartonRows(initialBatchId, 1, []));
    form.resetFields();
    form.setFieldsValue({
      batchId: initialBatchId || undefined,
      boxCount: 1,
      belongClientId: fixedClientId,
    });
  }, [open, defaultBatchId, fixedClientId, form]);

  const ensureProductsLoaded = () => {
    if (productsLoaded || productsLoading) return;
    setProductsLoading(true);
    productApi
      .list({ view: 'summary' })
      .then((res) => {
        setProductOptions(res.data || []);
        setProductsLoaded(true);
      })
      .catch(() => setProductOptions([]))
      .finally(() => setProductsLoading(false));
  };

  const syncCartonRows = useCallback((nextBatchId: string, nextBoxCount: number) => {
    setCartonRows((prev) => buildCartonRows(nextBatchId, nextBoxCount, prev));
  }, []);

  const onAdvancedChange = (checked: boolean) => {
    setAdvanced(checked);
    if (checked) {
      ensureProductsLoaded();
      syncCartonRows(batchId, boxCount);
    }
  };

  const onCartonIdChange = (index: number, cartonId: string) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const autoId = generateCartonId(batchId, index);
      next[index] = { ...next[index], cartonId, manualEdited: cartonId !== autoId };
      return next;
    });
  };

  const addProductToBox = (boxIndex: number, product: ProductDTO) => {
    const hscode = product.hscode;
    const nameEN = product.nameEN;
    if (!hscode || !nameEN) return;
    const key = productKey(nameEN, hscode);
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      if (row.products.some((p) => productKey(p.nameEN, p.hscode) === key)) {
        message.warning('该产品已添加');
        return prev;
      }
      next[boxIndex] = { ...row, products: [...row.products, { hscode, nameEN, amount: 1 }] };
      return next;
    });
  };

  const removeProductFromBox = (boxIndex: number, productIndex: number) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      next[boxIndex] = { ...row, products: row.products.filter((_, i) => i !== productIndex) };
      return next;
    });
  };

  const setProductAmount = (boxIndex: number, productIndex: number, amount: number) => {
    setCartonRows((prev) => {
      const next = [...prev];
      const row = next[boxIndex];
      if (!row) return prev;
      const products = [...row.products];
      if (!products[productIndex]) return prev;
      products[productIndex] = { ...products[productIndex], amount };
      next[boxIndex] = { ...row, products };
      return next;
    });
  };

  const submit = () => {
    form.validateFields().then((values) => {
      const trimmedBatchId = (values.batchId || '').trim();
      const count = values.boxCount ?? 1;
      let cartons: CreateCartonItemInBatch[] | undefined;
      if (advanced) {
        cartons = cartonRows.map((row) => ({
          cartonId: (row.cartonId || '').trim(),
          products: row.products.filter((p) => p.hscode && (p.amount ?? 0) > 0).map((p) => ({
            hscode: p.hscode,
            nameEN: p.nameEN,
            amount: p.amount,
          })),
        }));
        if (cartons.some((c) => !c.cartonId)) {
          message.warning('请填写所有箱号');
          return;
        }
        if (cartons.length !== count) {
          message.warning('箱列表数量与件数不一致');
          return;
        }
      }
      setSubmitting(true);
      cartonApi
        .createCartonBatch({
          batchId: trimmedBatchId,
          boxCount: count,
          fromSupplierId: values.fromSupplierId,
          belongClientId: fixedClientId || values.belongClientId,
          batchRemark: values.batchRemark?.trim() || undefined,
          cargoName: values.cargoName?.trim() || undefined,
          cartons,
          shipmentId: shipmentId || undefined,
        })
        .then((res) => {
          const bound = shipmentId ? `，已绑定运单 ${shipmentId}` : '';
          message.success(advanced
            ? `已录入批次 ${trimmedBatchId}，共 ${count} 箱（已生成单箱，状态待收货）${bound}`
            : `已录入批次 ${trimmedBatchId}，共 ${count} 件（未生成单箱）${bound}`);
          onCreated(res.data);
          onClose();
        })
        .catch((e) => message.error(e.response?.data?.message || '录入失败'))
        .finally(() => setSubmitting(false));
    });
  };

  return (
    <Modal
      title={title || '录入货物批次'}
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <p style={{ marginBottom: 12, color: '#666' }}>
        按批次登记货物：填写批次标识与件数即可，不生成单箱；单箱在同步代理入仓数据时匹配生成。
        如需录入时即生成每箱箱号与产品，请打开「高级选项」。
      </p>
      <Form form={form} layout="vertical">
        <Form.Item name="batchId" label="批次标识" rules={[{ required: true, message: '请填写批次标识' }]}>
          <Input
            placeholder="如：20260712-B1"
            onChange={(e) => {
              setBatchId(e.target.value);
              if (advanced) syncCartonRows(e.target.value, boxCount);
            }}
          />
        </Form.Item>
        <Space style={{ display: 'flex', width: '100%' }} align="start">
          <Form.Item name="boxCount" label="件数" initialValue={1} rules={[{ required: true, message: '请填写件数' }]} style={{ flex: 1 }}>
            <InputNumber
              min={1}
              max={500}
              style={{ width: '100%' }}
              onChange={(v) => {
                const c = v ?? 1;
                setBoxCount(c);
                if (advanced) syncCartonRows(batchId, c);
              }}
            />
          </Form.Item>
          <Form.Item name="fromSupplierId" label="供应商" rules={[{ required: true, message: '请选择供应商' }]} style={{ flex: 2 }}>
            <Select placeholder="选择供应商" showSearch optionFilterProp="label" options={suppliers} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="belongClientId" label="客户" rules={[{ required: true, message: '请选择客户' }]} style={{ flex: 2 }}>
            <Select
              placeholder="选择客户"
              showSearch
              optionFilterProp="label"
              options={customers}
              style={{ width: '100%' }}
              disabled={!!fixedClientId}
            />
          </Form.Item>
        </Space>
        <Form.Item name="cargoName" label="货物品名" {...cargoNameFormItemProps}>
          <CargoNameSelect />
        </Form.Item>
        <Form.Item name="batchRemark" label="批次备注">
          <Input.TextArea placeholder="批次备注信息（可选）" rows={2} />
        </Form.Item>
        <Form.Item label="高级选项：逐箱录入箱号与产品" style={{ marginBottom: 12 }}>
          <Switch checked={advanced} onChange={onAdvancedChange} />
        </Form.Item>
      </Form>

      {advanced && (
        <>
          <h4 style={{ marginBottom: 8 }}>箱号列表</h4>
          <AdaptiveTable
            size="small"
            pagination={false}
            dataSource={cartonRows.map((row, i) => ({ ...row, key: i }))}
            columns={[
              { title: '序号', width: 60, render: (_, __, index) => index + 1 },
              {
                title: '箱号',
                dataIndex: 'cartonId',
                render: (value, _, index) => (
                  <Input placeholder="箱号" value={value} onChange={(e) => onCartonIdChange(index, e.target.value)} />
                ),
              },
            ]}
            style={{ marginBottom: 16 }}
          />

          <h4 style={{ marginBottom: 8 }}>每箱产品（可选）</h4>
          <Collapse
            items={cartonRows.map((row, boxIndex) => ({
              key: String(boxIndex),
              label: `第 ${boxIndex + 1} 箱 · ${row.cartonId || '（未生成箱号）'}`,
              children: (
                <div>
                  <Select
                    placeholder="从已有产品中选择并添加"
                    allowClear
                    showSearch
                    loading={productsLoading}
                    optionFilterProp="label"
                    style={{ width: '100%', marginBottom: 8 }}
                    onChange={(key) => {
                      if (!key) return;
                      const { nameEN, hscode } = parseProductKey(key);
                      const p = productOptions.find((o) => o.nameEN === nameEN && o.hscode === hscode);
                      if (p) addProductToBox(boxIndex, p);
                    }}
                    options={productOptions
                      .filter((p) => p.hscode && p.nameEN)
                      .map((p) => ({
                        value: productKey(p.nameEN, p.hscode),
                        label: `${p.hscode} ${p.nameZN || p.nameEN || ''}`.trim(),
                      }))}
                  />
                  {row.products.length === 0 ? (
                    <div style={{ color: '#999', fontSize: 12 }}>暂无产品，可不填</div>
                  ) : (
                    row.products.map((p, pi) => (
                      <Space key={`${productKey(p.nameEN, p.hscode)}-${pi}`} style={{ marginBottom: 8 }}>
                        <Input value={`${p.hscode}${p.nameEN ? ` / ${p.nameEN}` : ''}`} disabled style={{ width: 220 }} />
                        <InputNumber
                          min={1}
                          value={p.amount ?? 1}
                          onChange={(v) => setProductAmount(boxIndex, pi, v ?? 1)}
                          style={{ width: 80 }}
                        />
                        <Button type="link" danger size="small" onClick={() => removeProductFromBox(boxIndex, pi)}>删除</Button>
                      </Space>
                    ))
                  )}
                </div>
              ),
            }))}
          />
        </>
      )}

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" loading={submitting} onClick={submit}>
          {shipmentId ? '新增并绑定' : '确认录入'}
        </Button>
        <Button onClick={onClose}>取消</Button>
      </Space>
    </Modal>
  );
};

export default CartonBatchFormModal;
